import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../api/client";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const STATUS_COLOR = {
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  confirmed: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  active: "bg-sky-400/10 text-sky-300 border-sky-400/20",
  completed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  cancelled: "bg-slate-400/10 text-slate-300 border-slate-400/20",
  rejected: "bg-red-400/10 text-red-300 border-red-400/20",
};

export default function CustomerDashboard() {
  const [bookings, setBookings] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings/my");
      setBookings(data);
    } catch (err) {
      console.error("[customer] failed to load bookings:", err?.message || err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try { await api.patch(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = {
    total: bookings.length,
    active: bookings.filter((b) => ["pending", "confirmed", "active"].includes(b.status)).length,
    completed: bookings.filter((b) => b.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Customer · Dashboard</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-10">My bookings.</h1>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <Stat label="Total" value={stats.total} />
          <Stat label="Active" value={stats.active} />
          <Stat label="Completed" value={stats.completed} />
        </div>

        {bookings.length === 0 ? (
          <div className="bg-[#0A111A]/60 border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-slate-400 mb-4">No bookings yet.</p>
            <Button onClick={() => (window.location.href = "/vehicles")} data-testid="empty-browse-btn" className="bg-sky-400 text-[#050B14] hover:bg-sky-300">
              Browse vehicles
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bg-[#0A111A] border border-white/5 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-5">
                <div className="w-full md:w-40 aspect-[16/10] rounded-lg overflow-hidden bg-[#050B14] flex-shrink-0">
                  {b.vehicle_image && <img src={b.vehicle_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium">{b.vehicle_name}</h3>
                    <Badge className={`${STATUS_COLOR[b.status]} border capitalize`}>{b.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-400">{b.pickup_location} → {b.drop_location}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {new Date(b.pickup_date).toLocaleString()} — {new Date(b.return_date).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col md:items-end gap-2">
                  <p className="text-xl font-light text-sky-300">₹{b.total_amount}</p>
                  {["pending", "confirmed"].includes(b.status) && (
                    <Button variant="outline" size="sm" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}
                      className="border-white/20 bg-transparent text-slate-300 hover:bg-white/5">Cancel</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A111A] border border-white/5 rounded-xl p-5">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</p>
      <p className="text-3xl font-light tracking-tight">{value}</p>
    </div>
  );
}
